/* eslint-disable */
import React from 'react'
import { FormattedMessage } from 'react-intl'

import SortingArrow from 'components/SortingArrow'
import sortDirection from 'constants/sortDirection'
import capitalizeString from 'utils/capitalize'
import ActionsMenu from 'features/ActionMenu'
import ApproveIcon from 'assets/icons/approve-user-icon'
import BlockIcon from 'assets/icons/block-icon'
import { SortingArrowWrapper } from 'components/SortingArrow/style'
import {
  SensitiveSpeciesStatusBadge
} from './style'

const FULL_ACCESS = 'FULL ACCESS'

const displayCountry = (countryData, countriesOptions) => {
  if (typeof countryData === 'string' && countryData) {
    return countryData
  }

  if (Array.isArray(countryData) && countryData.length > 0 && countriesOptions && countriesOptions.length > 0) {
    const countryNames = countryData
      .map((countryId) => {
        const country = countriesOptions.find((option) => option.value === countryId)
        return country?.label
      })
      .filter(Boolean)

    if (countryNames.length > 0) {
      return countryNames.length > 2 ? `${countryNames.slice(0, 2).join(', ')}...` : countryNames.join(', ')
    }
  }

  if ((typeof countryData === 'number' || typeof countryData === 'string') && countriesOptions && countriesOptions.length > 0) {
    const country = countriesOptions.find((option) => String(option.value) === String(countryData))
    return country?.label || '-'
  }

  return '-'
}

const normalizeSensitiveSpeciesList = (item) => {
  const raw = item.sensitiveSpeciesList ?? item.sensitive_species_list
  if (!Array.isArray(raw)) return []
  return raw.map((s) => ({
    taxonid: s.taxonid ?? s.taxonId,
    specieName: s.specieName ?? s.specie_name ?? '',
    scientificName: s.scientificName ?? s.scientific_name ?? '',
    sensitiveSpecies:
      s.sensitiveSpecies === true ||
      s.sensitive_species === true ||
      s.sensitiveSpecies === 'true' ||
      s.sensitive_species === 'true' ||
      s.sensitiveSpecies === 1 ||
      s.sensitive_species === 1,
    sensitiveAtSite:
      s.sensitive_at_site === true ||
      s.sensitive_at_site === 'true' ||
      s.sensitive_at_site === 1
  }))
}

const SensitiveSpeciesListCell = ({ list }) => {
  const items = Array.isArray(list) ? list : []
  const sensitiveItems = items.filter((s) => s.sensitiveSpecies || s.sensitiveAtSite)

  if (sensitiveItems.length === 0) return '-'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {sensitiveItems.map((s, idx) => {
        const name = s.scientificName || s.specieName || `Taxon ${s.taxonid ?? idx}`.trim()
        return (
          <div key={s.taxonid ?? idx} style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', lineHeight: '20px', color: '#202020' }}>{name}</span>
          </div>
        )
      })}
    </div>
  )
}

const RequestTypeBadge = ({ requestType }) => {
  const t = typeof requestType === 'string' ? requestType.trim() : ''
  const isFull = t === FULL_ACCESS
  const bg = isFull ? 'rgba(59, 130, 246, 0.12)' : 'rgba(124, 58, 237, 0.12)'
  const color = isFull ? 'rgba(29, 78, 216, 1)' : 'rgba(91, 33, 182, 1)'
  const labelId = isFull ? 'sensitiveSpeciesRequestTypeFull' : 'sensitiveSpeciesRequestTypePartial'
  const defaultMsg = isFull ? 'Full access' : 'Partial access'
  return (
    <SensitiveSpeciesStatusBadge style={{ background: bg, color }}>
      <FormattedMessage id={labelId} defaultMessage={defaultMsg} />
    </SensitiveSpeciesStatusBadge>
  )
}

const VisibilityStatusBadge = ({ statusColor, statusLabel }) => {
  const bg =
    statusColor === 'rgba(5, 148, 79, 1)'
      ? 'rgba(5, 148, 79, 0.12)'
      : statusColor === 'rgba(225, 25, 0, 1)'
        ? 'rgba(225, 25, 0, 0.12)'
        : 'rgba(255, 193, 7, 0.18)'

  return (
    <SensitiveSpeciesStatusBadge style={{ background: bg, color: statusColor }}>
      <FormattedMessage id={statusLabel} />
    </SensitiveSpeciesStatusBadge>
  )
}

export const makeSensitiveSitesRows = (
  sites,
  handleApprove,
  handleReject,
  countriesOptions = [],
  actionInProgressId = null
) => {
  return (sites || []).map((item) => {
    const siteHistory = item.SitesHistory || item
    const viewerId = item.viewerId
    const isActionInProgress = actionInProgressId != null && String(viewerId) === String(actionInProgressId)

    const requester = item.User || siteHistory?.User
    const site = siteHistory?.Site || item.Site || item

    const rawStatus =
      item.sensitiveSpeciesVisibilityStatus ??
      item.visibilityStatus ??
      siteHistory?.sensitiveSpeciesVisibilityStatus ??
      siteHistory?.visibilityStatus
    const visibilityApproved =
      item.visibilityApproved ?? item.visibility_approved ?? siteHistory?.visibilityApproved ?? siteHistory?.visibility_approved
    const isApproved =
      rawStatus === 'approved' || rawStatus === 'Approved' || visibilityApproved === true
    const isRejected =
      rawStatus === 'rejected' || rawStatus === 'Rejected' || visibilityApproved === false

    const statusLabel = isApproved
      ? 'sensitiveSpeciesStatusApproved'
      : isRejected
        ? 'sensitiveSpeciesStatusRejected'
        : 'sensitiveSpeciesStatusPending'
    const statusColor = isApproved ? 'rgba(5, 148, 79, 1)' : isRejected ? 'rgba(225, 25, 0, 1)' : 'rgba(255, 193, 7, 1)'

    const rawCountry =
      siteHistory?.country ??
      site?.country ??
      requester?.country ??
      item.country ??
      siteHistory?.countryId ??
      site?.countryId ??
      item.SiteDetailsCountryId ??
      item.countryId

    const sensitiveSpeciesList = normalizeSensitiveSpeciesList(item)
    const requestType = item.sensitiveSpeciesRequestType ?? item.sensitive_species_request_type

    const dropdownItems = []
    if (isApproved) {
      dropdownItems.push({
        icon: <BlockIcon />,
        title: 'sensitiveSpeciesRevoke',
        disabled: isActionInProgress,
        onClick: () => {
          if (isActionInProgress) return
          handleReject(item)
        }
      })
    } else if (isRejected) {
      dropdownItems.push({
        icon: <ApproveIcon />,
        title: 'approve',
        disabled: isActionInProgress,
        onClick: () => {
          if (isActionInProgress) return
          handleApprove(item)
        }
      })
    } else {
      dropdownItems.push(
        {
          icon: <ApproveIcon />,
          title: 'approve',
          disabled: isActionInProgress,
          onClick: () => {
            if (isActionInProgress) return
            handleApprove(item)
          }
        },
        {
          icon: <BlockIcon />,
          title: 'sensitiveSpeciesRejectAction',
          disabled: isActionInProgress,
          onClick: () => {
            if (isActionInProgress) return
            handleReject(item)
          }
        }
      )
    }

    return {
      id: viewerId ?? `${item.historyId}:${requestType}`,
      siteCode: site?.siteCode || siteHistory?.siteCode || item.siteCode || '-',
      siteName: siteHistory?.name || site?.name || item.siteName || item.name || '-',
      country: displayCountry(rawCountry, countriesOptions),
      sensitiveSpeciesList: <SensitiveSpeciesListCell list={sensitiveSpeciesList} />,
      requestType: <RequestTypeBadge requestType={requestType} />,
      requestingUserName: requester
        ? `${capitalizeString(requester.lastName || '')} ${capitalizeString(requester.firstName || '')}`
        : '-',
      requestingUserEmail: requester?.email || '-',
      visibilityStatus: (
        <VisibilityStatusBadge statusColor={statusColor} statusLabel={statusLabel} />
      ),
      action: <ActionsMenu dropdownItems={dropdownItems} />
    }
  })
}

export const makeSensitiveSitesColumns = (sorting, setSorting) => {
  const sortFunction = (sortBy) => {
    if (sorting.sortBy === sortBy) {
      if (sorting.sortDirection === sortDirection.ASC) {
        setSorting({ sortBy: sortBy, sortDirection: sortDirection.DESC })
      } else {
        setSorting({ sortBy: sortBy, sortDirection: sortDirection.ASC })
      }
    } else {
      setSorting({ sortBy: sortBy, sortDirection: sortDirection.ASC })
    }
  }

  return [
    {
      id: 'siteCode',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id="siteCode" />
          {
            <SortingArrow
              checked={sorting.sortDirection === sortDirection.ASC && sorting.sortBy === 'siteCode'}
              onChange={() => sortFunction('siteCode')}
            />
          }
        </SortingArrowWrapper>
      ),
      accessorKey: 'siteCode',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'siteName',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id="siteNameInternational" />
          {
            <SortingArrow
              checked={sorting.sortDirection === sortDirection.ASC && sorting.sortBy === 'siteName'}
              onChange={() => sortFunction('siteName')}
            />
          }
        </SortingArrowWrapper>
      ),
      accessorKey: 'siteName',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'country',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id="country" />
          {
            <SortingArrow
              checked={sorting.sortDirection === sortDirection.ASC && sorting.sortBy === 'country'}
              onChange={() => sortFunction('country')}
            />
          }
        </SortingArrowWrapper>
      ),
      accessorKey: 'country',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'sensitiveSpeciesList',
      header: <FormattedMessage id="sensitiveSpeciesListColumn" defaultMessage="Sensitive species" />,
      accessorKey: 'sensitiveSpeciesList',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'requestType',
      header: (
        <FormattedMessage
          id="sensitiveSpeciesRequestTypeColumn"
          defaultMessage="Request type"
        />
      ),
      accessorKey: 'requestType',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'requestingUserName',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage
            id="sensitiveSpeciesRequestingUserName"
            defaultMessage="Requesting user"
          />
          {
            <SortingArrow
              checked={sorting.sortDirection === sortDirection.ASC && sorting.sortBy === 'proposerName'}
              onChange={() => sortFunction('proposerName')}
            />
          }
        </SortingArrowWrapper>
      ),
      accessorKey: 'requestingUserName',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'requestingUserEmail',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage
            id="sensitiveSpeciesRequestingUserEmail"
            defaultMessage="Requesting user email"
          />
          {
            <SortingArrow
              checked={sorting.sortDirection === sortDirection.ASC && sorting.sortBy === 'proposerEmail'}
              onChange={() => sortFunction('proposerEmail')}
            />
          }
        </SortingArrowWrapper>
      ),
      accessorKey: 'requestingUserEmail',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'visibilityStatus',
      header: (
        <SortingArrowWrapper>
          <FormattedMessage id="sensitiveSpeciesVisibilityStatus" />
          {
            <SortingArrow
              checked={sorting.sortDirection === sortDirection.ASC && sorting.sortBy === 'visibilityStatus'}
              onChange={() => sortFunction('visibilityStatus')}
            />
          }
        </SortingArrowWrapper>
      ),
      accessorKey: 'visibilityStatus',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'action',
      header: <FormattedMessage id="actions" />,
      accessorKey: 'action',
      cell: ({ getValue }) => getValue()
    }
  ]
}
